import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, ClickAwayListener, type TextFieldProps } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker as MuiTimePicker } from '@mui/x-date-pickers/TimePicker';
import { renderDigitalClockTimeView } from '@mui/x-date-pickers/timeViewRenderers';
import { TimePicker as DsTimePicker } from '@natter-co/design-system';
import { ClearButton } from 'common/components/Buttons/ClearButton';
import { MenuItemWithText } from 'common/components/Menu/types';
import { VISUAL_REFRESH, isDefined } from 'common/utils';
import { addMinutes, format, isBefore, isValid, startOfDay } from 'date-fns';
import { debounce } from 'lodash-es';

import { TextField } from '../TextField';
import { DEFAULT_INPUT_TIME_FORMAT } from './constants';
import { StyledPopper } from './TimePicker.styled';
import { TimePickerSelect } from './TimePickerSelect';
import { toDsProps } from './toDsProps';
import { getDefaultScrollIndex, getNearestTimeOptionLabel } from './utils';

export const DEFAULT_TIME_STEPS_IN_MINUTES = 15;
const TOTAL_SLOTS = 96; // 24 hours * 4 slots per hour
const SLOTS_PER_HOUR = 60 / DEFAULT_TIME_STEPS_IN_MINUTES;
const DEFAULT_SCROLL_TO_HOUR = 9; // 9:00 AM

export type TimePickerProps = {
  error?: boolean;
  fullWidth?: boolean;
  /**
   * Drop the label once the field holds a value, which is what MUI's outlined field needs to stop
   * the notched label repeating the time below it.
   *
   * Honoured by the MUI branch ONLY. The refreshed field is a floating-label field: filled is the
   * state its label was drawn for, so it keeps showing it, shrunk, above the value.
   *
   * TODO(HNS-264): delete with the wrapper. This exists so a caller can ask for the legacy look
   * explicitly instead of signalling it by withholding `label` - which is what the occurrence rows
   * used to do, and which cost the date field beside these both its visible name and a stable
   * accessible one. See the note in `toDsProps`.
   */
  hideLabelWhenFilled?: boolean;
  hint?: string;
  label?: string;
  minTime?: Date;
  placeholder?: string;
  required?: boolean;
  value?: Date | null;
  disabled?: boolean;
  onAccept?(date: Date | null): void;
  onBlur?(): void;
  onClose?(): void;
  onChange(value: Date | null): void;
  onOpen?(): void;
};

export const TimePicker = forwardRef<HTMLInputElement, TimePickerProps>(
  (
    {
      error,
      fullWidth = true,
      hideLabelWhenFilled,
      hint,
      label,
      minTime,
      placeholder,
      required,
      value,
      disabled,
      onAccept,
      onBlur,
      onClose,
      onChange,
      onOpen,
    },
    ref
  ) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [activeValueIndex, setActiveValueIndex] = useState<number>();
    const [defaultScrollIndex, setDefaultScrollIndex] = useState<number>();
    const [tmpValue, setTmpValue] = useState<string>();
    const inputRef = useRef<HTMLInputElement>();

    const timeOptions = useMemo(() => {
      const startDate = startOfDay(minTime && isValid(minTime) ? minTime : new Date());

      return Array.from({ length: TOTAL_SLOTS })
        .map<MenuItemWithText | undefined>((_, index) => {
          const date = addMinutes(startDate, index * 15);

          if (minTime && isBefore(date, minTime)) return undefined;

          return {
            text: format(date, 'hh:mmaa'),
            onClick: () => {
              onChange(date);
              setActiveValueIndex(index);
            },
          };
        })
        .filter(isDefined<MenuItemWithText>);
    }, [onChange, minTime]);

    const handleAccept = useCallback(
      (date: Date | null) => {
        onChange(date);
        onAccept?.(date);
      },
      [onAccept, onChange]
    );

    const handleOpen = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        if (disabled) return;

        onOpen?.();
        setAnchorEl(event.currentTarget);

        // If no value is selected, scroll to 9:00 AM by default (or first available time if minTime is later)
        if (!value && activeValueIndex === undefined) {
          const scrollIndex = getDefaultScrollIndex(
            TOTAL_SLOTS,
            timeOptions.length,
            DEFAULT_SCROLL_TO_HOUR,
            SLOTS_PER_HOUR
          );
          setDefaultScrollIndex(scrollIndex);
        }
      },
      [onOpen, disabled, value, activeValueIndex, timeOptions]
    );

    const handleClose = useCallback(() => {
      setAnchorEl(null);
      onClose?.();
    }, [onClose]);

    const handleClear = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        onChange(null);
      },
      [onChange]
    );

    const onChangeDebounced = useMemo(
      () =>
        debounce(() => {
          setTmpValue(inputRef.current?.value);
        }, 100),
      []
    );

    useEffect(() => {
      if (!(value && isValid(value))) return;

      setTmpValue(format(value, 'hh:mm aa'));
    }, [value]);

    useEffect(() => {
      if (!tmpValue) return;

      const nearestTimeOptionLabel = getNearestTimeOptionLabel(tmpValue);
      if (!nearestTimeOptionLabel) return;
      setActiveValueIndex(timeOptions.findIndex((option) => option.text === nearestTimeOptionLabel));
    }, [tmpValue, timeOptions]);

    /**
     * The visual-refresh gate lives HERE, not at the call sites: they keep the MUI-shaped API and
     * never mention the flag. `toDsProps` returns `null` for anything DS 2.0 cannot express, which
     * falls through to the MUI-X implementation below.
     */
    const dsProps = VISUAL_REFRESH ? toDsProps({ error, fullWidth, hint, label, minTime, placeholder }) : null;

    if (dsProps) {
      return (
        // The wrapper `Box` stays. It is what `data-testid="TimePicker-input"` names, it is how
        // every test and every `Stack` around this field finds it, and the refreshed field is a
        // drop-in inside it rather than a replacement for it. No `ClickAwayListener` though - the
        // refreshed field's popup is a Base UI portal that dismisses itself, and wrapping it in
        // one would close the list on the very click that opened it.
        <Box data-testid="TimePicker-input" width="100%">
          <DsTimePicker
            {...dsProps}
            // Typed `HTMLInputElement` on both sides: the legacy wrapper hands this to MUI-X as
            // `inputRef`, and the refreshed field forwards it to its own control, which is the
            // same input. That is what keeps react-hook-form's focus-on-append working when a
            // host adds an occurrence.
            ref={ref}
            disabled={disabled}
            required={required}
            value={value}
            onBlur={onBlur}
            onOpenChange={(isOpen) => (isOpen ? onOpen?.() : onClose?.())}
            onValueChange={(date) => {
              onChange(date);
              onAccept?.(date);
            }}
          />
        </Box>
      );
    }

    return (
      <ClickAwayListener onClickAway={handleClose}>
        <Box data-testid="TimePicker-input" width={fullWidth ? '100%' : undefined}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MuiTimePicker
              format={DEFAULT_INPUT_TIME_FORMAT}
              inputRef={ref}
              // The legacy look: the value replaces the label rather than sitting under it.
              // Applied here rather than at the call site so BOTH branches can be driven by one
              // `label` prop - the refreshed field needs the real name in every state, and
              // inferring it from a missing prop is what broke the date field beside this one.
              label={hideLabelWhenFilled && value ? undefined : label}
              minTime={minTime}
              views={['hours']}
              slots={{
                popper: StyledPopper,
                textField: TextField,
              }}
              slotProps={{
                openPickerButton: {
                  sx: {
                    display: 'none',
                  },
                },
                textField: {
                  error,
                  fullWidth,
                  hint,
                  placeholder,
                  required,
                  suffix: value && !disabled ? <ClearButton onClick={handleClear} /> : undefined,
                  onBlur,
                  onClick: handleOpen,
                  inputRef,
                  inputProps: {
                    onChange: onChangeDebounced,
                  },
                } as unknown as TextFieldProps,
              }}
              value={value}
              onAccept={handleAccept}
              onChange={onChange}
              viewRenderers={{
                hours: renderDigitalClockTimeView,
                minutes: undefined,
              }}
              timeSteps={{ minutes: DEFAULT_TIME_STEPS_IN_MINUTES }}
              disabled={disabled}
            />
          </LocalizationProvider>
          <TimePickerSelect
            options={timeOptions}
            anchorEl={anchorEl}
            activeValueIndex={activeValueIndex}
            scrollToIndex={defaultScrollIndex}
            onClose={handleClose}
          />
        </Box>
      </ClickAwayListener>
    );
  }
);
